/**
 * Canonical website URL resolver and Web Share utility for CatchAI Text.
 * Ensures the share app link is always the genuine public address of the website.
 */

// Deployed public URL for CatchAI Text applet
export const CANONICAL_APP_URL = "https://catchaitext.com";

/**
 * Returns the optimized, public address of the website.
 * Resolves to the live shared cloud run address if currently on localhost or inside an internal preview frame,
 * or the active origin when deployed on a public domain.
 */
export function getWebsiteAddress(subpath: string = ""): string {
  let baseUrl = CANONICAL_APP_URL;

  if (typeof window !== "undefined" && window.location) {
    const origin = window.location.origin;
    // Check if origin is a valid production/public web address (not localhost or internal IP)
    if (
      origin &&
      !origin.includes("localhost") &&
      !origin.includes("127.0.0.1") &&
      !origin.includes("0.0.0.0") &&
      (origin.includes(".run.app") || origin.includes(".app") || origin.includes("catchaitext") || origin.includes(".com") || origin.includes(".org") || origin.includes(".edu"))
    ) {
      baseUrl = origin;
    }
  }

  if (!subpath) return baseUrl;
  const cleanSubpath = subpath.startsWith("/") || subpath.startsWith("#") ? subpath : `/${subpath}`;
  return `${baseUrl}${cleanSubpath}`;
}

/**
 * Native Web Share or Clipboard Fallback.
 */
export async function shareWebsite(options?: {
  title?: string;
  text?: string;
  subpath?: string;
  onSuccess?: (url: string) => void;
  onError?: (err: unknown) => void;
}): Promise<{ shared: boolean; copied: boolean; url: string }> {
  const url = getWebsiteAddress(options?.subpath);
  const title = options?.title || "CatchAI Text — Stylometric Text Analysis & Authorship Engine";
  const text = options?.text || "Open-access AI writing forensics and document authenticity verification platform.";

  // 1. Try native Web Share API (mobile devices, modern desktop browsers)
  if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
    try {
      await navigator.share({
        title,
        text,
        url
      });
      options?.onSuccess?.(url);
      return { shared: true, copied: false, url };
    } catch (err: any) {
      // If user cancelled the share dialog, do not error
      if (err?.name === "AbortError") {
        return { shared: false, copied: false, url };
      }
      // Otherwise fall through to clipboard copy
    }
  }

  // 2. Clipboard fallback
  try {
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      await navigator.clipboard.writeText(url);
      options?.onSuccess?.(url);
      return { shared: false, copied: true, url };
    }
  } catch (err) {
    options?.onError?.(err);
  }

  return { shared: false, copied: false, url };
}
